/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils-define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name hasEqualShape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/has-equal-shape}
*/
import hasEqualShape from '@stdlib/ndarray-base-assert-has-equal-shape';
setReadOnly( ns, 'hasEqualShape', hasEqualShape );

/**
* @name isAllowedDataTypeCast
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-allowed-data-type-cast}
*/
import isAllowedDataTypeCast from '@stdlib/ndarray-base-assert-is-allowed-data-type-cast';
setReadOnly( ns, 'isAllowedDataTypeCast', isAllowedDataTypeCast );

/**
* @name isBinaryDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-binary-data-type}
*/
import isBinaryDataType from '@stdlib/ndarray-base-assert-is-binary-data-type';
setReadOnly( ns, 'isBinaryDataType', isBinaryDataType );

/**
* @name isBooleanDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-boolean-data-type}
*/
import isBooleanDataType from '@stdlib/ndarray-base-assert-is-boolean-data-type';
setReadOnly( ns, 'isBooleanDataType', isBooleanDataType );

/**
* @name isBooleanIndexDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-boolean-index-data-type}
*/
import isBooleanIndexDataType from '@stdlib/ndarray-base-assert-is-boolean-index-data-type';
setReadOnly( ns, 'isBooleanIndexDataType', isBooleanIndexDataType );

/**
* @name isBufferLengthCompatible
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-buffer-length-compatible}
*/
import isBufferLengthCompatible from '@stdlib/ndarray-base-assert-is-buffer-length-compatible';
setReadOnly( ns, 'isBufferLengthCompatible', isBufferLengthCompatible );

/**
* @name isBufferLengthCompatibleShape
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-buffer-length-compatible-shape}
*/
import isBufferLengthCompatibleShape from '@stdlib/ndarray-base-assert-is-buffer-length-compatible-shape';
setReadOnly( ns, 'isBufferLengthCompatibleShape', isBufferLengthCompatibleShape );

/**
* @name isCastingMode
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-casting-mode}
*/
import isCastingMode from '@stdlib/ndarray-base-assert-is-casting-mode';
setReadOnly( ns, 'isCastingMode', isCastingMode );

/**
* @name isColumnMajor
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-column-major}
*/
import isColumnMajor from '@stdlib/ndarray-base-assert-is-column-major';
setReadOnly( ns, 'isColumnMajor', isColumnMajor );

/**
* @name isColumnMajorContiguous
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-column-major-contiguous}
*/
import isColumnMajorContiguous from '@stdlib/ndarray-base-assert-is-column-major-contiguous';
setReadOnly( ns, 'isColumnMajorContiguous', isColumnMajorContiguous );

/**
* @name isColumnMajorString
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-column-major-string}
*/
import isColumnMajorString from '@stdlib/ndarray-base-assert-is-column-major-string';
setReadOnly( ns, 'isColumnMajorString', isColumnMajorString );

/**
* @name isComplexFloatingPointDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-complex-floating-point-data-type}
*/
import isComplexFloatingPointDataType from '@stdlib/ndarray-base-assert-is-complex-floating-point-data-type';
setReadOnly( ns, 'isComplexFloatingPointDataType', isComplexFloatingPointDataType );

/**
* @name isComplexFloatingPointDataTypeChar
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-complex-floating-point-data-type-char}
*/
import isComplexFloatingPointDataTypeChar from '@stdlib/ndarray-base-assert-is-complex-floating-point-data-type-char';
setReadOnly( ns, 'isComplexFloatingPointDataTypeChar', isComplexFloatingPointDataTypeChar );

/**
* @name isContiguous
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-contiguous}
*/
import isContiguous from '@stdlib/ndarray-base-assert-is-contiguous';
setReadOnly( ns, 'isContiguous', isContiguous );

/**
* @name isDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-data-type}
*/
import isDataType from '@stdlib/ndarray-base-assert-is-data-type';
setReadOnly( ns, 'isDataType', isDataType );

/**
* @name isDataTypeObject
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-data-type-object}
*/
import isDataTypeObject from '@stdlib/ndarray-base-assert-is-data-type-object';
setReadOnly( ns, 'isDataTypeObject', isDataTypeObject );

/**
* @name isDataTypeString
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-data-type-string}
*/
import isDataTypeString from '@stdlib/ndarray-base-assert-is-data-type-string';
setReadOnly( ns, 'isDataTypeString', isDataTypeString );

/**
* @name isEqualDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-equal-data-type}
*/
import isEqualDataType from '@stdlib/ndarray-base-assert-is-equal-data-type';
setReadOnly( ns, 'isEqualDataType', isEqualDataType );

/**
* @name isFloatingPointDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-floating-point-data-type}
*/
import isFloatingPointDataType from '@stdlib/ndarray-base-assert-is-floating-point-data-type';
setReadOnly( ns, 'isFloatingPointDataType', isFloatingPointDataType );

/**
* @name isGenericDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-generic-data-type}
*/
import isGenericDataType from '@stdlib/ndarray-base-assert-is-generic-data-type';
setReadOnly( ns, 'isGenericDataType', isGenericDataType );

/**
* @name isIndexDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-index-data-type}
*/
import isIndexDataType from '@stdlib/ndarray-base-assert-is-index-data-type';
setReadOnly( ns, 'isIndexDataType', isIndexDataType );

/**
* @name isIndexMode
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-index-mode}
*/
import isIndexMode from '@stdlib/ndarray-base-assert-is-index-mode';
setReadOnly( ns, 'isIndexMode', isIndexMode );

/**
* @name isInputCastingPolicy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-input-casting-policy}
*/
import isInputCastingPolicy from '@stdlib/ndarray-base-assert-is-input-casting-policy';
setReadOnly( ns, 'isInputCastingPolicy', isInputCastingPolicy );

/**
* @name isIntegerDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-integer-data-type}
*/
import isIntegerDataType from '@stdlib/ndarray-base-assert-is-integer-data-type';
setReadOnly( ns, 'isIntegerDataType', isIntegerDataType );

/**
* @name isIntegerIndexDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-integer-index-data-type}
*/
import isIntegerIndexDataType from '@stdlib/ndarray-base-assert-is-integer-index-data-type';
setReadOnly( ns, 'isIntegerIndexDataType', isIntegerIndexDataType );

/**
* @name isMaskIndexDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-mask-index-data-type}
*/
import isMaskIndexDataType from '@stdlib/ndarray-base-assert-is-mask-index-data-type';
setReadOnly( ns, 'isMaskIndexDataType', isMaskIndexDataType );

/**
* @name isMostlySafeDataTypeCast
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-mostly-safe-data-type-cast}
*/
import isMostlySafeDataTypeCast from '@stdlib/ndarray-base-assert-is-mostly-safe-data-type-cast';
setReadOnly( ns, 'isMostlySafeDataTypeCast', isMostlySafeDataTypeCast );

/**
* @name isNumericDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-numeric-data-type}
*/
import isNumericDataType from '@stdlib/ndarray-base-assert-is-numeric-data-type';
setReadOnly( ns, 'isNumericDataType', isNumericDataType );

/**
* @name isOrder
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-order}
*/
import isOrder from '@stdlib/ndarray-base-assert-is-order';
setReadOnly( ns, 'isOrder', isOrder );

/**
* @name isOutputDataTypePolicy
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-output-data-type-policy}
*/
import isOutputDataTypePolicy from '@stdlib/ndarray-base-assert-is-output-data-type-policy';
setReadOnly( ns, 'isOutputDataTypePolicy', isOutputDataTypePolicy );

/**
* @name isReadOnly
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-read-only}
*/
import isReadOnly from '@stdlib/ndarray-base-assert-is-read-only';
setReadOnly( ns, 'isReadOnly', isReadOnly );

/**
* @name isRealDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-real-data-type}
*/
import isRealDataType from '@stdlib/ndarray-base-assert-is-real-data-type';
setReadOnly( ns, 'isRealDataType', isRealDataType );

/**
* @name isRealFloatingPointDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-real-floating-point-data-type}
*/
import isRealFloatingPointDataType from '@stdlib/ndarray-base-assert-is-real-floating-point-data-type';
setReadOnly( ns, 'isRealFloatingPointDataType', isRealFloatingPointDataType );

/**
* @name isRowMajor
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-row-major}
*/
import isRowMajor from '@stdlib/ndarray-base-assert-is-row-major';
setReadOnly( ns, 'isRowMajor', isRowMajor );

/**
* @name isRowMajorContiguous
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-row-major-contiguous}
*/
import isRowMajorContiguous from '@stdlib/ndarray-base-assert-is-row-major-contiguous';
setReadOnly( ns, 'isRowMajorContiguous', isRowMajorContiguous );

/**
* @name isRowMajorString
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-row-major-string}
*/
import isRowMajorString from '@stdlib/ndarray-base-assert-is-row-major-string';
setReadOnly( ns, 'isRowMajorString', isRowMajorString );

/**
* @name isSafeDataTypeCast
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-safe-data-type-cast}
*/
import isSafeDataTypeCast from '@stdlib/ndarray-base-assert-is-safe-data-type-cast';
setReadOnly( ns, 'isSafeDataTypeCast', isSafeDataTypeCast );

/**
* @name isSameKindDataTypeCast
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-same-kind-data-type-cast}
*/
import isSameKindDataTypeCast from '@stdlib/ndarray-base-assert-is-same-kind-data-type-cast';
setReadOnly( ns, 'isSameKindDataTypeCast', isSameKindDataTypeCast );

/**
* @name isScalarMostlySafeCompatible
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-scalar-mostly-safe-compatible}
*/
import isScalarMostlySafeCompatible from '@stdlib/ndarray-base-assert-is-scalar-mostly-safe-compatible';
setReadOnly( ns, 'isScalarMostlySafeCompatible', isScalarMostlySafeCompatible );

/**
* @name isSignedIntegerDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-signed-integer-data-type}
*/
import isSignedIntegerDataType from '@stdlib/ndarray-base-assert-is-signed-integer-data-type';
setReadOnly( ns, 'isSignedIntegerDataType', isSignedIntegerDataType );

/**
* @name isSingleSegmentCompatible
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-single-segment-compatible}
*/
import isSingleSegmentCompatible from '@stdlib/ndarray-base-assert-is-single-segment-compatible';
setReadOnly( ns, 'isSingleSegmentCompatible', isSingleSegmentCompatible );

/**
* @name isStructDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-struct-data-type}
*/
import isStructDataType from '@stdlib/ndarray-base-assert-is-struct-data-type';
setReadOnly( ns, 'isStructDataType', isStructDataType );

/**
* @name isUnsignedIntegerDataType
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/ndarray/base/assert/is-unsigned-integer-data-type}
*/
import isUnsignedIntegerDataType from '@stdlib/ndarray-base-assert-is-unsigned-integer-data-type';
setReadOnly( ns, 'isUnsignedIntegerDataType', isUnsignedIntegerDataType );


// EXPORTS //

export default ns;
